import deCalendarLocale from "@fullcalendar/core/locales/de";
import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Sidebar from "../common/sidebar";
import { useSelector } from "react-redux";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import moment from "moment-timezone";
import { alterTimeFormat } from "../common/displayTime";
import Footer from "../common/footer";
import ScrollToTopButton from "../common/ScrollToTopButton";
import "../../assets/css/calendar.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(secs) {
  const s = Number(secs);
  if (!s || s <= 0) return "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function defaultColor(color) {
  return color && color !== "null" && color !== "" ? color : "#1a5c30";
}

function readableTextColor(color) {
  if (!color || typeof color !== "string") return "#111827";

  const hex = color.trim();
  if (!hex.startsWith("#")) return "#111827";

  const normalized = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;

  if (!/^#[0-9a-fA-F]{6}$/.test(normalized)) return "#111827";

  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.62 ? "#111827" : "#ffffff";
}

function hexToRgb(color) {
  if (!color || typeof color !== "string") return null;

  const hex = color.trim();
  if (!hex.startsWith("#")) return null;

  const normalized = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;

  if (!/^#[0-9a-fA-F]{6}$/.test(normalized)) return null;

  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function rgba(color, alpha) {
  const rgb = hexToRgb(color);
  if (!rgb) return `rgba(26, 92, 48, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

const MERGE_GAP_SECONDS = 120;

function isMergeCandidate(prevItem, nextItem, getDuration) {
  if (!prevItem || !nextItem) return false;

  const prevTitle = prevItem.title || "(no title)";
  const nextTitle = nextItem.title || "(no title)";
  const prevProject = prevItem.project_name || "No project";
  const nextProject = nextItem.project_name || "No project";
  const prevColor = defaultColor(prevItem.color);
  const nextColor = defaultColor(nextItem.color);
  const prevBillable = !!prevItem.is_billable;
  const nextBillable = !!nextItem.is_billable;
  const prevDay = moment.unix(prevItem.timer_start).format("YYYY-MM-DD");
  const nextDay = moment.unix(nextItem.timer_start).format("YYYY-MM-DD");
  const prevEnd = prevItem.timer_end || (prevItem.timer_start + getDuration(prevItem));
  const nextStart = nextItem.timer_start;
  const gap = nextStart - prevEnd;

  return (
    prevTitle === nextTitle &&
    prevProject === nextProject &&
    prevColor === nextColor &&
    prevBillable === nextBillable &&
    prevDay === nextDay &&
    gap >= 0 &&
    gap <= MERGE_GAP_SECONDS
  );
}

function mergeCalendarEvents(rawItems, getDuration) {
  const sorted = [...rawItems].sort((a, b) => a.timer_start - b.timer_start);
  const clusters = [];

  sorted.forEach((item) => {
    const lastCluster = clusters[clusters.length - 1];
    const lastItem = lastCluster?.items[lastCluster.items.length - 1];

    if (lastCluster && isMergeCandidate(lastItem, item, getDuration)) {
      lastCluster.items.push(item);
      lastCluster.end = Math.max(lastCluster.end, item.timer_end || (item.timer_start + getDuration(item)));
      return;
    }

    clusters.push({
      items: [item],
      start: item.timer_start,
      end: item.timer_end || (item.timer_start + getDuration(item)),
    });
  });

  return clusters.map((cluster) => {
    const first = cluster.items[0];
    const last = cluster.items[cluster.items.length - 1];
    const projectColor = defaultColor(first.color);
    const duration = Math.max(0, cluster.end - cluster.start);

    return {
      id             : cluster.items.length > 1
                         ? `merged-${first.timer_id}-${last.timer_id}`
                         : first.timer_id,
      title          : first.title || "(no title)",
      start          : moment.unix(cluster.start).format(),
      end            : moment.unix(cluster.end).format(),
      backgroundColor: projectColor,
      borderColor    : projectColor,
      extendedProps  : {
        projectName   : first.project_name || "No project",
        projectColor,
        textColor     : readableTextColor(projectColor),
        duration,
        isBillable    : first.is_billable,
        mergedCount   : cluster.items.length,
        mergedEntries : cluster.items.map((item) => ({
          id       : item.timer_id,
          start    : item.timer_start,
          end      : item.timer_end || (item.timer_start + getDuration(item)),
          duration : getDuration(item),
          title    : item.title || "(no title)",
        })),
      },
    };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReportCalendar() {
  useTranslation();
  const auth        = useSelector((state) => state.auth);
  const calendarRef = useRef();

  // Calendar data
  const [events,    setEvents]    = useState([]);
  const [dayTotals, setDayTotals] = useState({});   // { 'YYYY-MM-DD': secs }
  const [summary,   setSummary]   = useState(null);  // { totalSecs, projects[] }
  const [loading,   setLoading]   = useState(false);

  // Event detail popup
  const [popup, setPopup] = useState({ show: false });

  // User preferences
  const [timeFormat,      setTimeFormat]      = useState("HH:mm");
  const [eventTimeFormat, setEventTimeFormat] = useState({ hour: "2-digit", minute: "2-digit", meridiem: false });
  const [firstDayOfWeek,  setFirstday]        = useState(0);
  const [userTz,          setUserTz]          = useState(() => moment.tz.guess());

  // ── Init user preferences ────────────────────────────────────────────────
  useEffect(() => {
    const props = auth.user_properties;
    if (props) {
      const tz = props.timeZone ? props.timeZone.split(" ")[0] : moment.tz.guess();
      moment.tz.setDefault(tz);
      setUserTz(tz);
      setTimeFormat(alterTimeFormat(props.timeFormat));
      setEventTimeFormat(
        props.timeFormat === "12-hour"
          ? { hour: "numeric", minute: "2-digit", meridiem: "short" }
          : { hour: "2-digit", minute: "2-digit", meridiem: false }
      );
      setFirstday(props.firstDayOfWeek || 0);
    } else {
      moment.tz.setDefault();
    }
  }, [auth.user_properties]);

  // ── Fetch events for current calendar range ──────────────────────────────
  const fetchEvents = useCallback(async () => {
    if (!calendarRef.current) return;
    setLoading(true);
    try {
      const calApi   = calendarRef.current.getApi();
      const tzOffset = calApi.view.currentStart.getTimezoneOffset() * 60000;

      const start = new Date(calApi.view.currentStart.getTime() - tzOffset)
        .toISOString().split("T")[0];

      const actualEnd = moment(calApi.view.currentEnd).subtract(1, "days");
      const end = new Date(actualEnd._d.getTime() - calApi.view.currentEnd.getTimezoneOffset() * 60000)
        .toISOString().split("T")[0];

      const res = await axios.post(
        "/api/report/calendar_data",
        JSON.stringify({ start, end, workspace_id: auth.workspace_id }),
        { headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" } }
      );

      if (res.status !== 200) return;

      const rawItems = res.data.results || [];

      // timerTime is stored in milliseconds → convert to seconds
      // timer_end may be null on running/incomplete timers → fall back to timerTime
      const getDuration = (item) => {
        const ms = parseInt(item.timerTime);
        if (ms > 0) return Math.round(ms / 1000);
        return Math.max(0, (item.timer_end || 0) - (item.timer_start || 0));
      };

      const viewType = calApi.view.type;
      const calEvents = (viewType === "timeGridWeek" || viewType === "timeGridDay")
        ? mergeCalendarEvents(rawItems, getDuration)
        : rawItems.map((item) => ({
            id             : item.timer_id,
            title          : item.title || "(no title)",
            start          : moment.unix(item.timer_start).format(),
            end            : item.timer_end
                               ? moment.unix(item.timer_end).format()
                               : moment.unix(item.timer_start + getDuration(item)).format(),
            backgroundColor: defaultColor(item.color),
            borderColor    : defaultColor(item.color),
            extendedProps  : {
              projectName   : item.project_name || "No project",
              projectColor  : defaultColor(item.color),
              textColor     : readableTextColor(defaultColor(item.color)),
              duration      : getDuration(item),
              isBillable    : item.is_billable,
              mergedCount   : 1,
              mergedEntries : [{
                id       : item.timer_id,
                start    : item.timer_start,
                end      : item.timer_end || (item.timer_start + getDuration(item)),
                duration : getDuration(item),
                title    : item.title || "(no title)",
              }],
            },
          }));
      setEvents(calEvents);

      // Build day totals map
      const totals = {};
      rawItems.forEach((item) => {
        const day  = moment.unix(item.timer_start).format("YYYY-MM-DD");
        const secs = getDuration(item);
        totals[day] = (totals[day] || 0) + secs;
      });
      setDayTotals(totals);

      // Build summary (total + per-project breakdown)
      const projMap = {};
      rawItems.forEach((item) => {
        const name = item.project_name || "No project";
        const secs = getDuration(item);
        if (!projMap[name]) projMap[name] = { name, color: defaultColor(item.color), secs: 0 };
        projMap[name].secs += secs;
      });
      const totalSecs = Object.values(projMap).reduce((a, p) => a + p.secs, 0);
      const projects  = Object.values(projMap).sort((a, b) => b.secs - a.secs);
      setSummary({ totalSecs, projects });

    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [auth.token, auth.workspace_id]);

  // Initial load once calendarRef is ready
  useEffect(() => {
    if (calendarRef.current) fetchEvents();
  }, [calendarRef, fetchEvents]);

  // Close popup on outside click
  useEffect(() => {
    const handler = () => setPopup({ show: false });
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  // Close event detail popup on Escape
  useEffect(() => {
    if (!popup.show) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPopup({ show: false });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [popup.show]);

  // ── Event content renderer ───────────────────────────────────────────────
  function renderEventContent(eventInfo) {
    const dur  = formatDuration(eventInfo.event.extendedProps.duration);
    const view = eventInfo.view.type;
    const mergedCount = eventInfo.event.extendedProps.mergedCount || 1;
    return (
      <div className="cal-event-inner">
        <span className="cal-event-dot" />
        <span className="cal-event-title">{eventInfo.event.title}</span>
        {(view === "timeGridWeek" || view === "timeGridDay") && (
          <span className="cal-event-dur">{dur}</span>
        )}
        {view === "dayGridMonth" && (
          <span className="cal-event-dur">{dur}</span>
        )}
        {(view === "timeGridWeek" || view === "timeGridDay") && mergedCount > 1 && (
          <span className="cal-event-count" title={`${mergedCount} merged entries`}>
            {mergedCount}
          </span>
        )}
      </div>
    );
  }

  // ── Day cell content (day number + total badge) ──────────────────────────
  function renderDayCellContent(arg) {
    const dateStr = moment(arg.date).format("YYYY-MM-DD");
    const total   = dayTotals[dateStr];
    return (
      <>
        <span>{arg.dayNumberText}</span>
        {total > 0 && (
          <span className="cal-day-total-badge">{formatDuration(total)}</span>
        )}
      </>
    );
  }

  // ── Event click → show popup ─────────────────────────────────────────────
  function handleEventClick(info) {
    info.jsEvent.stopPropagation();
    const rect  = info.el.getBoundingClientRect();
    const props = info.event.extendedProps;
    const popupWidth = 280;
    const popupHeight = 190;
    const gap = 10;

    // Decide popup X position — keep within viewport and use fixed coords
    let x = rect.left;
    if (x + popupWidth > window.innerWidth - 12) {
      x = window.innerWidth - popupWidth - 12;
    }
    if (x < 12) x = 12;

    // Place above the event if enough space, otherwise below
    let y = rect.top - popupHeight - gap;
    if (y < 12) {
      y = rect.bottom + gap;
    }
    const maxTop = window.innerHeight - popupHeight - 12;
    if (y > maxTop) y = Math.max(12, maxTop);

    setPopup({
      show        : true,
      x,
      y,
      title       : info.event.title,
      projectName : props.projectName,
      projectColor: props.projectColor,
      start       : moment(info.event.startStr).format(timeFormat),
      end         : moment(info.event.endStr).format(timeFormat),
      duration    : props.duration,
      isBillable  : props.isBillable,
      mergedCount : props.mergedCount || 1,
    });
  }

  function handleEventDidMount(info) {
    const color = info.event.extendedProps.projectColor || defaultColor(info.event.backgroundColor);
    const textColor = info.event.extendedProps.textColor || readableTextColor(color);

    info.el.style.setProperty("--cal-event-color", color);
    info.el.style.setProperty("--cal-event-text", textColor);
    info.el.style.setProperty("--cal-event-soft", rgba(color, 0.14));
    info.el.style.setProperty("--cal-event-soft-strong", rgba(color, 0.24));
    info.el.style.setProperty("--cal-event-soft-border", rgba(color, 0.28));
  }

  // ── Navigate helpers ─────────────────────────────────────────────────────
  function navAction(fn) {
    return async function () {
      const api = calendarRef.current.getApi();
      fn(api);
      // Let FullCalendar re-render one tick before reading new range
      await new Promise((r) => setTimeout(r, 50));
      fetchEvents();
    };
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <Sidebar />

      {/* Page header */}
      <div className="header">
        <div className="container-fluid">
          <div className="row" style={{ flexDirection: "column" }}>
            <div className="reports-header-contain">
              <h3>{tr("Calendar")}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="content-wrap" id="wrapper">
        <div className="main">
          <div className="cal-page">

            {/* ── Summary bar ────────────────────────────────────── */}
            {summary && (
              <div className="cal-summary-bar">
                <div className="cal-summary-total">
                  <span className="cal-summary-total-label">{tr("Period Total")}</span>
                  <span className="cal-summary-total-value">
                    {formatDuration(summary.totalSecs)}
                  </span>
                </div>
                <div className="cal-summary-projects">
                  {summary.totalSecs === 0 ? (
                    <span className="cal-summary-empty">{tr("No entries in this period")}</span>
                  ) : (
                    summary.projects.slice(0, 6).map((p) => (
                      <div key={p.name} className="cal-summary-proj-pill">
                        <span
                          className="cal-summary-proj-dot"
                          style={{ background: p.color }}
                        />
                        {p.name}
                        <span className="cal-summary-proj-time">
                          {formatDuration(p.secs)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ── Calendar card ───────────────────────────────────── */}
            <div className="cal-card">

              {/* Loading overlay */}
              {loading && (
                <div className="cal-loading-overlay">
                  <div className="cal-spinner" />
                </div>
              )}

              <FullCalendar
                locales={[deCalendarLocale]}
                locale={getLocale() === "de-DE" ? "de" : "en-gb"}
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView="dayGridMonth"
                events={events}
                eventContent={renderEventContent}
                eventDidMount={handleEventDidMount}
                dayCellContent={renderDayCellContent}
                dayMaxEvents={3}
                firstDay={firstDayOfWeek}
                eventTimeFormat={eventTimeFormat}
                timeZone={userTz}
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                scrollTime="00:00:00"
                scrollTimeReset={false}
                slotDuration="00:15:00"
                slotLabelInterval="01:00:00"
                eventMinHeight={22}
                eventClick={handleEventClick}
                headerToolbar={{
                  left  : "today prev,next",
                  center: "title",
                  right : "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                customButtons={{
                  prev: {
                    icon : "chevron-left",
                    click: navAction((api) => api.prev()),
                  },
                  next: {
                    icon : "chevron-right",
                    click: navAction((api) => api.next()),
                  },
                  today: {
                    get text() { return tr("Today"); },
                    click: navAction((api) => api.today()),
                  },
                  dayGridMonth: {
                    get text() { return tr("Month"); },
                    click: navAction((api) => api.changeView("dayGridMonth")),
                  },
                  timeGridWeek: {
                    get text() { return tr("Week"); },
                    click: navAction((api) => api.changeView("timeGridWeek")),
                  },
                  timeGridDay: {
                    get text() { return tr("Day"); },
                    click: navAction((api) => api.changeView("timeGridDay")),
                  },
                }}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ── Event detail popup ───────────────────────────────────────────── */}
      {popup.show && (
        <div
          className="cal-popup"
          style={{ left: popup.x, top: popup.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Project name */}
          <div className="cal-popup-project">
            <span
              className="cal-popup-proj-dot"
              style={{ background: popup.projectColor }}
            />
            <span className="cal-popup-proj-name">{popup.projectName}</span>
          </div>

          {/* Title */}
          <div className="cal-popup-title">{popup.title}</div>

          {/* Time range */}
          <div className="cal-popup-row">
            <span className="cal-popup-icon">🕐</span>
            {popup.start} → {popup.end}
          </div>

          {/* Duration */}
          <div className="cal-popup-row">
            <span className="cal-popup-icon">⏱</span>
            <span className="cal-popup-dur">{formatDuration(popup.duration)}</span>
          </div>

          {popup.mergedCount > 1 && (
            <div className="cal-popup-row">
              <span className="cal-popup-icon">↔</span>
              <span>{popup.mergedCount} {tr("merged entries")}</span>
            </div>
          )}

          {/* Billable badge */}
          <div>
            <span
              className={`cal-popup-badge ${
                popup.isBillable ? "cal-popup-badge--billable" : "cal-popup-badge--nonbillable"
              }`}
            >
              {popup.isBillable ? tr("● Billable") : tr("○ Non-billable")}
            </span>
          </div>
        </div>
      )}

      <ScrollToTopButton />
    </div>
  );
}
